/**
 * Site Media Service (web).
 *
 * Wraps the `/site-media/*` backend endpoints. Endpoints MUST NOT include
 * the `/api/` prefix — axios `api` client already adds it.
 */

import api from './api'

const LOCAL_MULTIPART_CAP = 40 * 1024 * 1024 // 40MB

export async function listSiteMedia(filters = {}) {
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== null && v !== undefined && v !== '')
  )
  const { data } = await api.get('/site-media/', { params })
  return data
}

export async function getSiteMedia(id) {
  const { data } = await api.get(`/site-media/${id}/`)
  return data
}

export async function uploadSiteMediaMultipart(payload, onProgress) {
  const form = new FormData()
  form.append('project', String(payload.project ?? payload.projectId ?? ''))
  form.append('media_type', payload.media_type || payload.mediaType)
  form.append('capture_source', payload.capture_source || payload.captureSource || 'web')
  form.append('consent_ack', payload.consent_ack === false ? 'false' : 'true')
  form.append('captured_at', payload.captured_at || payload.capturedAt || new Date().toISOString())

  const deviceId = payload.device_id || payload.deviceId
  if (deviceId) form.append('device_id', deviceId)

  const lat = payload.geo_lat ?? payload.geo?.lat
  const lng = payload.geo_lng ?? payload.geo?.lng
  const accuracy = payload.geo_accuracy_m ?? payload.geo?.accuracyM
  if (lat != null) form.append('geo_lat', String(lat))
  if (lng != null) form.append('geo_lng', String(lng))
  if (accuracy != null) form.append('geo_accuracy_m', String(accuracy))

  const duration = payload.duration_sec ?? payload.durationSec
  if (duration != null) form.append('duration_sec', String(duration))

  const checkReq = payload.check_requirement ?? payload.checkRequirementId
  if (checkReq) form.append('check_requirement', String(checkReq))
  const bankDraw = payload.bank_draw ?? payload.bankDrawId
  if (bankDraw) form.append('bank_draw', String(bankDraw))
  const ctx = payload.related_context ?? payload.relatedContextId
  if (ctx) form.append('related_context', String(ctx))
  if (payload.notes) form.append('notes', payload.notes)
  if (payload.mime_type || payload.mimeType) {
    form.append('mime_type', payload.mime_type || payload.mimeType)
  }
  form.append('file', payload.file, payload.filename || fileNameFor(payload))

  const { data } = await api.post('/site-media/upload/', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (evt) => {
      if (!onProgress || !evt?.total) return
      onProgress(Math.round((evt.loaded / evt.total) * 100))
    },
  })
  return data
}

export async function uploadSiteMediaPresigned(payload, onProgress) {
  const body = {
    project: payload.project ?? payload.projectId,
    media_type: payload.media_type || payload.mediaType,
    filename: payload.filename || fileNameFor(payload),
    content_type: payload.mime_type || payload.mimeType || guessMime(payload),
    capture_source: payload.capture_source || payload.captureSource || 'web',
    device_id: payload.device_id || payload.deviceId || '',
    captured_at: payload.captured_at || payload.capturedAt || new Date().toISOString(),
    consent_ack: payload.consent_ack === false ? false : true,
    related_context: payload.related_context ?? payload.relatedContextId ?? null,
    check_requirement: payload.check_requirement ?? payload.checkRequirementId ?? null,
    bank_draw: payload.bank_draw ?? payload.bankDrawId ?? null,
    geo_lat: payload.geo_lat ?? payload.geo?.lat ?? null,
    geo_lng: payload.geo_lng ?? payload.geo?.lng ?? null,
    geo_accuracy_m: payload.geo_accuracy_m ?? payload.geo?.accuracyM ?? null,
    duration_sec: payload.duration_sec ?? payload.durationSec ?? null,
    notes: payload.notes || '',
  }

  const { data: presign } = await api.post('/site-media/presign/', body)
  await putToS3(
    presign.upload_url,
    payload.file,
    presign.headers?.['Content-Type'] || body.content_type,
    onProgress
  )
  const { data } = await api.post(`/site-media/${presign.media_id}/finalize/`, {
    file_size: payload.file?.size,
  })
  return data
}

export async function uploadSiteMedia(payload, onProgress) {
  const size = payload.file?.size || 0
  if (size > LOCAL_MULTIPART_CAP) return uploadSiteMediaPresigned(payload, onProgress)
  return uploadSiteMediaMultipart(payload, onProgress)
}

export async function attachSiteMedia(id, opts = {}) {
  const body = {}
  if (opts.checkRequirementId !== undefined || opts.check_requirement !== undefined) {
    body.check_requirement = opts.checkRequirementId ?? opts.check_requirement
  }
  if (opts.bankDrawId !== undefined || opts.bank_draw !== undefined) {
    body.bank_draw = opts.bankDrawId ?? opts.bank_draw
  }
  if (opts.relatedContextId !== undefined || opts.related_context !== undefined) {
    body.related_context = opts.relatedContextId ?? opts.related_context
  }
  if (opts.notes !== undefined) body.notes = opts.notes
  const { data } = await api.post(`/site-media/${id}/attach/`, body)
  return data
}

export async function deleteSiteMedia(id) {
  await api.delete(`/site-media/${id}/`)
}

function fileNameFor({ mediaType, media_type, filename }) {
  if (filename) return filename
  const mt = mediaType || media_type
  const ext = { photo: 'jpg', video: 'mp4', audio: 'm4a' }[mt] || 'bin'
  return `site-media-${Date.now()}.${ext}`
}

function guessMime({ mediaType, media_type, mimeType, mime_type, file }) {
  if (mime_type) return mime_type
  if (mimeType) return mimeType
  if (file?.type) return file.type
  const mt = mediaType || media_type
  return { photo: 'image/jpeg', video: 'video/mp4', audio: 'audio/m4a' }[mt] || 'application/octet-stream'
}

async function putToS3(url, file, contentType, onProgress) {
  if (typeof XMLHttpRequest === 'undefined') {
    const res = await fetch(url, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file })
    if (!res.ok) throw new Error(`S3 PUT failed: ${res.status} ${res.statusText}`)
    return
  }
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url, true)
    xhr.setRequestHeader('Content-Type', contentType)
    xhr.upload.onprogress = (evt) => {
      if (!onProgress || !evt.lengthComputable) return
      onProgress(Math.round((evt.loaded / evt.total) * 100))
    }
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 PUT ${xhr.status}`)))
    xhr.onerror = () => reject(new Error('S3 PUT network error'))
    xhr.send(file)
  })
}

export default {
  listSiteMedia,
  getSiteMedia,
  uploadSiteMedia,
  uploadSiteMediaMultipart,
  uploadSiteMediaPresigned,
  attachSiteMedia,
  deleteSiteMedia,
}
